// vg-binding: CONTEXT:decisions  (P999.11.D-09/D-14/D-15/D-16 tokens + D-04 dark-ok)
// vg-binding: INTERFACE-STANDARDS:error-shape  (pure FE restyle — no API surface changed)
// apps/admin/src/pages/billing/TransferGroupsPage.tsx
//
// Phase 8.2 Wave 3 Task 21 — Admin Transfer Groups CRUD + members + history.
//
// Per CONTEXT.md:
//   - Transfer Group whitelist + per-member limits.
//   - Per-member transfer-block flag (read-only here; toggle UI
//                 lives in 08.1 Merchant Detail). Members dialog row renders
//                 the flag derived from MerchantTransferBlock.
//
// Originally landed in Phase 3.2 Wave 11 Task 33 (G-). Phase 8.2
// rework extracts the form + members dialog into reusable feature
// components and adds the spec test IDs:
//   - transfer-group-create-btn
//   - transfer-group-edit-btn-{groupId}
//   - transfer-group-deactivate-btn-{groupId}
//   - transfer-group-members-dialog          (owned by features/billing/TransferGroupMembersDialog)
//   - transfer-group-member-search-input     (owned by features/billing/TransferGroupMembersDialog)
//
// Endpoints (per API-CONTRACTS §Finance — Transfer Groups):
//   GET    /api/v1/admin/transfer-groups
//   POST   /api/v1/admin/transfer-groups
//   PATCH  /api/v1/admin/transfer-groups/:id
//   POST   /api/v1/admin/transfer-groups/:id/members
//   DELETE /api/v1/admin/transfer-groups/:id/members/:merchant_id
//   GET    /api/v1/admin/transfer-groups/:id/history?...&export=csv
//
// Acceptance criteria (PLAN/task-21.md):
//   ✓ Create / Edit / Deactivate flows wired (no hard delete — soft-deactivate
//     via PATCH is_active=false; matches immutability).
//   ✓ Members dialog supports add + remove + per-member limit edit (via
//     remove+re-add) + per-member transfer-block flag indicator (read-only).
//   ✓ Deactivated groups read-only (action buttons + members dialog disabled).
//   ✓ Cross-link to Transactions filtered by group (from members dialog).
//
// vg-binding: PLAN:task-21
// vg-binding: P8.D-89
// vg-binding: P8.D-458

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Button,
  cn,
  useToast,
  PageHeader,
} from '@printway/ui';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { apiClient } from '@printway/shared/api';
import { useTranslation } from '../../i18n/useTranslation.js';
import { PageListFooter } from '../../components/datatable/PageListFooter.js';

import {
  TransferGroupForm,
  type TransferGroupFormState,
} from '../../features/billing/TransferGroupForm.js';
import { TransferGroupHistoryDialog } from '../../features/billing/TransferGroupHistoryDialog.js';
import {
  TransferGroupMembersDialog,
  type TransferGroupSummary,
} from '../../features/billing/TransferGroupMembersDialog.js';

// ---------------------------------------------------------------------------
// Wire types — slim FE mirror of API serializer shape.
// ---------------------------------------------------------------------------

interface TransferGroup extends TransferGroupSummary {
  description: string | null;
  min_amount: number | null;
  max_amount: number | null;
  owner_email: string | null;
  per_member_limit_cents: number | null;
  created_at: string;
}

interface SuccessEnvelope<T> {
  success: true;
  data: T;
  requestId: string;
}
interface ErrorEnvelope {
  success: false;
  error: { code: string; message: string };
  requestId: string;
}

function unwrap<T>(payload: unknown): T {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as SuccessEnvelope<T>).success === true &&
    'data' in payload
  ) {
    return (payload as SuccessEnvelope<T>).data;
  }
  return payload as T;
}

interface ApiError {
  code?: string;
  message: string;
  status: number;
}

function envelopeError(payload: unknown, status: number, t: (key: string) => string): ApiError {
  if (
    payload &&
    typeof payload === 'object' &&
    'success' in payload &&
    (payload as ErrorEnvelope).success === false &&
    'error' in payload
  ) {
    const err = (payload as ErrorEnvelope).error;
    return { code: err?.code, message: err?.message ?? t('transfer_group.toast.unknownError'), status };
  }
  return { message: t('transfer_group.toast.networkErrorDesc'), status };
}

function formatUsd(cents: number): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

function idemKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `tg-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

export function TransferGroupsPage(): JSX.Element {
  const { toast } = useToast();
  const { t } = useTranslation('finance');
  const [groups, setGroups] = useState<TransferGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const [formState, setFormState] = useState<TransferGroupFormState | null>(null);
  const [memberGroup, setMemberGroup] = useState<TransferGroup | null>(null);
  // D-70 (G-63): per-group transfer history + CSV export dialog.
  const [historyGroup, setHistoryGroup] = useState<TransferGroup | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<TransferGroup | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Phase 8.2 audit 2026-05-23 — URL-synced page + pageSize per /stores
  // standard. BE returns the full whitelist as an array (no total / page
  // meta), so paging is applied client-side to keep the Rows-per-page
  // selector reachable.
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(
    1,
    Number.parseInt(searchParams.get('page') ?? '1', 10) || 1,
  );
  const rawPageSize = Number.parseInt(
    searchParams.get('pageSize') ?? '25',
    10,
  );
  const pageSize = Number.isFinite(rawPageSize) && rawPageSize > 0
    ? Math.min(100, Math.max(10, rawPageSize))
    : 25;
  const setPage = (nextPage: number): void => {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete('page');
    else next.set('page', String(nextPage));
    setSearchParams(next, { replace: true });
  };
  const handlePageSizeChange = (size: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('pageSize', String(size));
    next.delete('page');
    setSearchParams(next, { replace: true });
  };
  const total = groups.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const visibleGroups = useMemo(
    () => groups.slice((page - 1) * pageSize, page * pageSize),
    [groups, page, pageSize],
  );

  const fetchGroups = useCallback(async (): Promise<void> => {
    setLoading(true);
    try {
      const client = apiClient();
      const path = '/api/v1/admin/transfer-groups?limit=100';
      const { data, error, response } = await (
        client as unknown as {
          GET: (p: string) => Promise<{
            data?: unknown;
            error?: unknown;
            response: Response;
          }>;
        }
      ).GET(path);
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.toast.loadFailed'),
          description: env.message,
          variant: 'error',
        });
        setGroups([]);
        return;
      }
      // P3 Bug 17 (debug-bill iter 10, 2026-05-04): envelope is
      // `{ success, data: TransferGroup[] }` — unwrap returns the array
      // directly. Reading `.data` on the array yielded undefined → blank table.
      const payload = unwrap<TransferGroup[]>(data);
      setGroups(Array.isArray(payload) ? payload : []);
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description: err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
      setGroups([]);
    } finally {
      setLoading(false);
    }
  }, [toast, t]);

  useEffect(() => {
    void fetchGroups();
  }, [fetchGroups, refreshTick]);

  async function handleConfirmDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      // PATCH `is_active=false` is the soft-delete path — no hard
      // DELETE for groups (immutability). Only members can be removed.
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(deleteTarget.id)}`;
      const { error, response } = await (
        client as unknown as {
          PATCH: (
            p: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).PATCH(path, {
        body: { is_active: false },
        headers: { 'Idempotency-Key': idemKey() },
      });
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.toast.deactivateFailed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('transfer_group.toast.deactivated'),
        description: deleteTarget.name,
        variant: 'success',
      });
      setDeleteTarget(null);
      setRefreshTick((t) => t + 1);
    } finally {
      setDeleting(false);
    }
  }

  // Reactivate a deactivated group — PATCH is_active=true (symmetric to the
  // soft-deactivate above; BE keeps history + members intact). dbg-20260723.
  async function handleReactivate(g: TransferGroup): Promise<void> {
    try {
      const client = apiClient();
      const path = `/api/v1/admin/transfer-groups/${encodeURIComponent(g.id)}`;
      const { error, response } = await (
        client as unknown as {
          PATCH: (
            p: string,
            init: { body: unknown; headers?: Record<string, string> },
          ) => Promise<{ data?: unknown; error?: unknown; response: Response }>;
        }
      ).PATCH(path, {
        body: { is_active: true },
        headers: { 'Idempotency-Key': idemKey() },
      });
      if (error || !response.ok) {
        const env = envelopeError(error, response.status, t);
        toast({
          title: t('transfer_group.toast.reactivateFailed'),
          description: env.message,
          variant: 'error',
        });
        return;
      }
      toast({
        title: t('transfer_group.toast.reactivated'),
        description: g.name,
        variant: 'success',
      });
      setRefreshTick((x) => x + 1);
    } catch (err) {
      toast({
        title: t('transfer_group.toast.networkError'),
        description:
          err instanceof Error ? err.message : t('transfer_group.toast.checkConnection'),
        variant: 'error',
      });
    }
  }

  return (
    <main className="flex flex-col gap-6 p-6" data-testid="admin-transfer-groups">
      <PageHeader
        title={t('transfer_group.title')}
        subtitle={t('transfer_group.page.description')}
        actions={
          <Button
            onClick={() => setFormState({ mode: 'create' })}
            data-testid="transfer-group-create-btn"
          >
            + {t('transfer_group.actions.create')}
          </Button>
        }
      />

      {/* Phase 8.2 debug 2026-05-23 bug 3 — replaced the bespoke grid layout
          (which drifted whenever a column's content was narrower than its
          header label) with a proper `<table>` element. Columns are sized
          via fixed widths so header + body cells always line up regardless
          of row content. */}
      <div className="overflow-x-auto rounded-xl border border-pw-border bg-pw-bg">
        <table className="min-w-full table-fixed text-sm">
          <thead className="bg-pw-surface/40 text-xs font-medium uppercase tracking-wider text-pw-text-muted">
            <tr>
              <th className="w-[18%] px-4 py-3 text-left">{t('transfer_group.columns.name')}</th>
              <th className="w-[24%] px-4 py-3 text-left">{t('transfer_group.columns.description')}</th>
              <th className="w-[8%] px-4 py-3 text-left">{t('transfer_group.columns.memberCount')}</th>
              <th className="w-[18%] px-4 py-3 text-left">{t('transfer_group.columns.owner')}</th>
              <th className="w-[12%] px-4 py-3 text-left">{t('transfer_group.columns.perMemberLimit')}</th>
              <th className="w-[8%] px-4 py-3 text-left">{t('transfer_group.columns.status')}</th>
              <th className="w-[12%] px-4 py-3 text-right">{t('transfer_group.columns.actions')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-pw-border">
            {loading ? (
              <tr>
                <td
                  colSpan={7}
                  className="h-32 px-4 text-center text-sm text-pw-text-muted"
                >
                  {t('transfer_group.loading')}
                </td>
              </tr>
            ) : groups.length === 0 ? (
              <tr>
                <td
                  colSpan={7}
                  className="h-32 px-4 text-center text-sm text-pw-text-muted"
                >
                  {t('transfer_group.empty')}
                </td>
              </tr>
            ) : (
              visibleGroups.map((g) => (
                <tr
                  key={g.id}
                  className="hover:bg-pw-surface/30"
                  data-testid={`tg-row-${g.id}`}
                >
                  <td className="truncate px-4 py-3 text-sm font-semibold text-pw-text">
                    {g.name}
                  </td>
                  <td
                    className="truncate px-4 py-3 text-sm text-pw-text-muted"
                    title={g.description ?? undefined}
                  >
                    {g.description || '—'}
                  </td>
                  <td className="px-4 py-3 text-sm text-pw-text">
                    {g.member_count}
                  </td>
                  <td
                    className="truncate px-4 py-3 text-sm text-pw-text"
                    data-testid={`tg-owner-${g.id}`}
                    title={g.owner_email ?? undefined}
                  >
                    {g.owner_email ?? '—'}
                  </td>
                  <td
                    className="px-4 py-3 text-sm text-pw-text"
                    data-testid={`tg-per-member-limit-${g.id}`}
                  >
                    {g.per_member_limit_cents != null
                      ? formatUsd(g.per_member_limit_cents)
                      : '—'}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex h-6 items-center rounded-md px-2 text-[10px] font-semibold uppercase tracking-wider',
                        g.is_active
                          ? 'bg-emerald-100 dark:bg-emerald-950/40 text-emerald-700 dark:text-emerald-300'
                          : 'bg-pw-surface text-pw-text-muted',
                      )}
                    >
                      {g.is_active
                        ? t('transfer_group.status.active')
                        : t('transfer_group.status.inactive')}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex justify-end gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setMemberGroup(g)}
                        data-testid={`transfer-group-members-btn-${g.id}`}
                      >
                        {t('transfer_group.actions.members')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setHistoryGroup(g)}
                        data-testid={`transfer-group-history-btn-${g.id}`}
                      >
                        {t('transfer_group.actions.history')}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          setFormState({ mode: 'edit', group: g })
                        }
                        data-testid={`transfer-group-edit-btn-${g.id}`}
                      >
                        {t('transfer_group.actions.edit')}
                      </Button>
                      {g.is_active ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-rose-600 dark:text-rose-300 hover:bg-rose-50"
                          onClick={() => setDeleteTarget(g)}
                          data-testid={`transfer-group-deactivate-btn-${g.id}`}
                        >
                          {t('transfer_group.actions.deactivate')}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-600 dark:text-emerald-300 hover:bg-emerald-50"
                          onClick={() => void handleReactivate(g)}
                          data-testid={`transfer-group-reactivate-btn-${g.id}`}
                        >
                          {t('transfer_group.actions.reactivate')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {/* Pagination — canonical /stores footer (Rows-per-page + Total +
            numbered) per audit 2026-05-23 Cluster A. Client-side paging
            because the BE list endpoint returns the full whitelist as an
            array (limit=100 hardcoded). */}
        {total > 0 ? (
          <div className="border-t border-pw-border px-6 py-3">
            <PageListFooter
              page={page}
              pageSize={pageSize}
              total={total}
              totalPages={totalPages}
              loading={loading}
              onPageChange={setPage}
              onPageSizeChange={handlePageSizeChange}
              testId="transfer-groups-footer"
            />
          </div>
        ) : null}
      </div>

      <TransferGroupForm
        state={formState}
        onClose={() => setFormState(null)}
        onSaved={() => setRefreshTick((t) => t + 1)}
      />

      <TransferGroupMembersDialog
        group={memberGroup}
        onClose={() => {
          setMemberGroup(null);
          // Refresh list so member_count updates after drawer changes.
          setRefreshTick((t) => t + 1);
        }}
      />

      <TransferGroupHistoryDialog
        group={
          historyGroup ? { id: historyGroup.id, name: historyGroup.name } : null
        }
        onClose={() => setHistoryGroup(null)}
      />

      {deleteTarget ? (
        <ConfirmDialog
          open={!!deleteTarget}
          onOpenChange={(o) => !o && setDeleteTarget(null)}
          type="destructive"
          title={t('transfer_group.confirm.deactivateTitle', {
            name: deleteTarget.name,
          })}
          description={t('transfer_group.confirm.deactivateDesc')}
          confirmLabel={t('transfer_group.actions.deactivate')}
          loading={deleting}
          onConfirm={handleConfirmDelete}
        />
      ) : null}
    </main>
  );
}

export default TransferGroupsPage;
